import { useMutation, useQuery } from '@apollo/client';
import { Check } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FormSchemaProvider } from '@procureai/ui-form-mui';
import { useConfirmationDialog, useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { isNil, isNotNil } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PROCUREMENT_KNOWLEDGE_LIST_QUERY } from '../../ProcurementKnowledgeListQuery.gql';
import { ProcurementKnowledgeForm } from '../ProcurementKnowledgeForm/ProcurementKnowledgeForm';
import {
  ProcurementKnowledgeDocumentData,
  procurementKnowledgeDocumentDataDefault,
  procurementKnowledgeDocumentSchema,
} from '../ProcurementKnowledgeForm/ProcurementKnowledgeForm.schema';
import { SvbrgDocumentVariant, SvbrgLanguage } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { OpenCloseProps } from '~/hooks';

import {
  PROCUREMENT_KNOWLEDGE_ADD_MUTATION,
  PROCUREMENT_KNOWLEDGE_UPDATE_METADATA_MUTATION,
} from './ProcurementKnowledgeAddMutation.gql';
import { PROCUREMENT_KNOWLEDGE_DELETE_MUTATION } from './ProcurementKnowledgeDeleteMutation.gql';
import { PROCUREMENT_KNOWLEDGE_DETAILS_QUERY } from './ProcurementKnowledgeDocumentDetailsQuery.gql';

export interface ProcurementKnowledgeEditDialogProps extends OpenCloseProps {
  docId: string;
}

export const ProcurementKnowledgeEditDialog = ({ docId, open, onClose }: ProcurementKnowledgeEditDialogProps) => {
  const formId = useUuid();

  const { onError } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();

  const { t } = useTranslation(I18nNamespace.knowledgeRepository, {
    keyPrefix: 'ProcurementKnowledge.ProcurementKnowledgeEditDialog',
  });

  // get document details
  const { data, loading } = useQuery(PROCUREMENT_KNOWLEDGE_DETAILS_QUERY, {
    variables: {
      docId,
    },
    skip: !open,
  });

  // extract loaded document
  const doc = data?.sevenbridges.document;

  // empty default values
  const defaultValues = useMemo(() => {
    return procurementKnowledgeDocumentDataDefault(
      doc && {
        fileId: doc.fileId,
        title: doc.title,
        description: doc.description ?? '',
        content: doc.content ?? '',
        url: doc.url ?? '',
        variant: doc.variant,
        language: doc.language,
      },
    );
  }, [doc]);

  // mutation to upload new file
  const [updateDocument, { loading: updating }] = useMutation(PROCUREMENT_KNOWLEDGE_ADD_MUTATION, {
    onError,
  });

  // mutation to update metadata only (when fileId unchanged)
  const [updateMetadata, { loading: updatingMetadata }] = useMutation(PROCUREMENT_KNOWLEDGE_UPDATE_METADATA_MUTATION, {
    onError,
  });

  const [deleteDocument, { loading: deleting }] = useMutation(PROCUREMENT_KNOWLEDGE_DELETE_MUTATION, {
    onError,
  });

  const handleUpdateDocument = async (data: ProcurementKnowledgeDocumentData) => {
    if (!doc?.id) return;

    const fileIdChanged = data.fileId !== doc.fileId;

    if (fileIdChanged) {
      // New file uploaded - use upload mutation
      const res = await updateDocument({
        variables: {
          input: {
            id: docId,
            fileId: data.fileId,
            title: data.title,
            description: data.description,
            url: data.url,
            variant: data.variant as SvbrgDocumentVariant,
            language: data.language as SvbrgLanguage,
            content: data.content,
          },
        },
        update(cache, { data }) {
          const result = data?.sevenbridges.document.upload;
          if (!result) return;
          cache.modify({
            id: cache.identify({ __typename: 'SvbrgDocumentListItem', id: result.id }),
            fields: {
              fileId: () => result.fileId,
              fileName: () => result.fileName,
              title: () => result.title,
              description: () => result.description,
              url: () => result.url,
              variant: () => result.variant,
              language: () => result.language,
            },
          });
        },
      });
      if (res.data?.sevenbridges.document.upload) {
        onClose();
      }
    } else {
      // Only metadata changed - use updateMetadata mutation
      const res = await updateMetadata({
        variables: {
          input: {
            id: docId,
            title: data.title,
            description: data.description,
            url: data.url,
            variant: data.variant as SvbrgDocumentVariant,
            language: data.language as SvbrgLanguage,
            content: data.content,
          },
        },
        update(cache, { data }) {
          const result = data?.sevenbridges.document.updateMetadata;
          if (!result) return;
          cache.modify({
            id: cache.identify({ __typename: 'SvbrgDocumentListItem', id: result.id }),
            fields: {
              title: () => result.title,
              description: () => result.description,
              url: () => result.url,
              variant: () => result.variant,
              language: () => result.language,
            },
          });
        },
      });
      if (res.data?.sevenbridges.document.updateMetadata) {
        onClose();
      }
    }
  };

  const handleDelete = () => {
    confirm(
      async () => {
        if (doc) {
          // delete the document
          const res = await deleteDocument({
            variables: {
              docId: doc.id,
            },
            update(cache, { data, errors }) {
              // Verify no error occurred while deleting the document
              // Check on keptDocuments is a nil-safeguard for typing only.
              // If errors is nil, keptDocuments is always defined (at least an empty array)
              const keptDocuments = data?.sevenbridges.document.delete;
              if (isNotNil(errors) || isNil(keptDocuments)) return;

              const foundQuery = cache.readQuery({ query: PROCUREMENT_KNOWLEDGE_LIST_QUERY });

              const previous = foundQuery?.sevenbridges.documents;
              if (!previous) return;

              // Filter out the deleted document from the cache and update the query:
              const filteredNodes = previous?.nodes.filter((item) => item.id !== doc.id);
              cache.writeQuery({
                query: PROCUREMENT_KNOWLEDGE_LIST_QUERY,
                data: {
                  sevenbridges: {
                    documents: {
                      nodes: filteredNodes,
                      totalCount: filteredNodes.length,
                    },
                  },
                },
              });
            },
          });

          if (res.data?.sevenbridges.document.delete) {
            onClose?.();
          }
        }
      },
      {
        maxWidth: 'xs',
        title: t(`delete.confirm.title`),
        message: t(`delete.confirm.message`),
      },
    )();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>{t('title')}</DialogTitle>

      <DialogContent>
        {loading ? (
          <CircularProgressCentered />
        ) : (
          <FormSchemaProvider<ProcurementKnowledgeDocumentData>
            id={formId}
            schema={procurementKnowledgeDocumentSchema}
            defaultValues={defaultValues}
            onSubmit={handleUpdateDocument}
          >
            <ProcurementKnowledgeForm />
          </FormSchemaProvider>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          disabled={!doc || deleting}
          loading={deleting}
          onClick={handleDelete}
          sx={{ mr: 'auto' }}
        >
          {t('delete.label')}
        </Button>

        <Button variant="outlined" onClick={onClose}>
          {t('cancelLabel')}
        </Button>
        <Button
          loading={updating || updatingMetadata}
          variant="contained"
          color="primary"
          type="submit"
          startIcon={<Check />}
          form={formId}
          disabled={updating || updatingMetadata || !doc}
        >
          {t('confirmLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
